// Receipt/Invoice AI Scan — review modal (docs/RECEIPT_SCAN_MODULE.md). Uploads a receipt/invoice,
// lets the AI extraction come back, then shows one editable row per line item with a green/red
// %-diff badge against the ingredient's last known per-kg price before batch-confirming into N
// IngredientReceivingLog rows.
import React, { useState, useEffect, useRef } from 'react';
import receiptScanService from '../services/receiptScanService';
import ingredientService from '../services/ingredientService';
import qboService from '../services/qboService';
import SupplierPickerField from './SupplierPickerField';
import SearchableSelect from './SearchableSelect';
import { usePermissions } from '../context/PermissionContext';

const LB_TO_KG = 0.453592;
// Local calendar date as "YYYY-MM-DD" — NOT `toISOString().split('T')[0]`, which converts to the
// UTC calendar date and silently shows "tomorrow" for part of the evening in a US timezone.
const toLocalDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const today = () => toLocalDateStr(new Date());
// Mirrors the backend's isPlausibleDeliveryDate bound (ingredientReceivingRoutes.js) — a stray
// AI-OCR-misread receipt date has shipped as a receiving log's delivery date before.
const maxDeliveryDate = (() => {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  return toLocalDateStr(d);
})();

// Every column in the line-item table starts with an invisible spacer so real inputs line up on
// the same baseline across columns, regardless of what (if anything) renders above them in each
// cell. Fixed height + hidden overflow, rather than minHeight, so a taller content type (e.g. the
// Ingredient column's receipt-text badge) can never push its column out of alignment with the rest.
const ROW_LABEL_STYLE = { height: 16, lineHeight: '16px', fontSize: 10, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' };

const qtyKgOf = (quantity, unit) => {
  const q = parseFloat(quantity);
  if (!q || isNaN(q)) return null;
  return unit === 'lb' ? q * LB_TO_KG : q;
};

// A container (e.g. one shipment of flour) can split across several real lot codes — mirrors the
// {lotCode, quantityKg} list pattern already used by the manual receiving log form
// (IngredientInventoryPage.js), so a line's total quantity is the sum of its lot rows.
const sumLotQtyKg = (lotCodes, unit) =>
  (lotCodes || []).reduce((sum, lc) => sum + (qtyKgOf(lc.quantityKg, unit) || 0), 0);

const getLoggedInUsername = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return '';
    const decoded = JSON.parse(atob(token.split('.')[1]));
    return decoded?.name || decoded?.username || '';
  } catch {
    return '';
  }
};

function IngredientLinePicker({ ingredients, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef();

  const selected = ingredients.find(i => String(i.id) === String(value));

  useEffect(() => {
    setQuery(selected ? selected.name : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery(selected ? selected.name : '');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selected]);

  const filtered = query.trim()
    ? ingredients.filter(i =>
        i.name.toLowerCase().includes(query.trim().toLowerCase()) ||
        (i.rawMaterialCode && i.rawMaterialCode.toLowerCase().includes(query.trim().toLowerCase()))
      )
    : ingredients;

  return (
    <div ref={containerRef} className="position-relative">
      <input
        className="form-control form-control-sm"
        placeholder="Search ingredient…"
        value={open ? query : (selected ? selected.name : '')}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={e => setQuery(e.target.value)}
        autoComplete="off"
      />
      {open && (
        <ul className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 1060, maxHeight: 200, overflowY: 'auto' }}>
          {value && (
            <li className="list-group-item list-group-item-action py-1 small text-muted"
              onMouseDown={() => { onChange(''); setOpen(false); }}>
              — Clear —
            </li>
          )}
          {filtered.map(i => (
            <li key={i.id} className="list-group-item list-group-item-action py-1 small"
              onMouseDown={() => { onChange(String(i.id)); setOpen(false); }}>
              {i.name}{i.rawMaterialCode ? ` (${i.rawMaterialCode})` : ''}
            </li>
          ))}
          {filtered.length === 0 && <li className="list-group-item py-1 small text-muted">No matching ingredient.</li>}
        </ul>
      )}
    </div>
  );
}

function PctDiffBadge({ pct }) {
  if (pct == null || !isFinite(pct)) return <span className="badge bg-secondary">No prior price</span>;
  const rounded = Math.round(pct * 10) / 10;
  const cls = rounded > 0.5 ? 'bg-danger' : rounded < -0.5 ? 'bg-success' : 'bg-secondary';
  const sign = rounded > 0 ? '+' : '';
  return <span className={`badge ${cls}`}>{sign}{rounded}% vs last</span>;
}

export default function ReceiptScanReviewModal({ file, ingredients, suppliers, fetchSuppliers, onClose, onConfirmed }) {
  const { companySettings, role } = usePermissions();
  const [phase, setPhase] = useState('uploading'); // uploading | scanning | review | confirming | done | error
  const [error, setError] = useState('');
  const [scanId, setScanId] = useState(null);
  const [meta, setMeta] = useState(null);
  const [priceMap, setPriceMap] = useState({}); // ingredientId -> pricePerKg
  const [confirmResult, setConfirmResult] = useState(null); // { receivingLogIds, qboPurchaseId, qboPushError, r365InvoiceId, r365PushError }

  const [supplierId, setSupplierId] = useState('');
  const [deliveryDate, setDeliveryDate] = useState(today());
  const [receivedByName, setReceivedByName] = useState(getLoggedInUsername());
  const [lines, setLines] = useState([]);
  const [taxAmount, setTaxAmount] = useState('');
  const startedRef = useRef(false);

  // QuickBooks push (docs/RECEIPT_SCAN_MODULE.md's Phase 2) — opt-in per receipt, defaulting to
  // the company's configured Purchase Account but overridable here (not every purchase goes on
  // the same card). Hidden entirely when QBO isn't connected, and for non-admins — QBO access is
  // admin-only everywhere else in this app (routes/qboRoutes.js), and this is exactly the "QA
  // confirms, admin pushes later" split the feature exists for.
  const qboConnected = !!companySettings?.qbRealmId && role === 'admin';
  const [qboBankAccounts, setQboBankAccounts] = useState([]);
  const [pushToQbo, setPushToQbo] = useState(false);
  const [qbPurchaseAccountId, setQbPurchaseAccountId] = useState('');

  useEffect(() => {
    if (!qboConnected) return;
    qboService.getAccounts(['Bank', 'Credit Card']).then(accounts => {
      setQboBankAccounts(accounts);
      if (companySettings.qbPurchaseAccountId) {
        setQbPurchaseAccountId(String(companySettings.qbPurchaseAccountId));
        setPushToQbo(true);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qboConnected]);

  // Restaurant 365 push — same opt-in-per-receipt, admin-only shape as the QBO push above, just
  // with no bank-account picker (R365's free-text Location/GL Account live in Company Settings
  // only; there's no API to populate a live options list the way QBO's accounts endpoint does).
  const r365Connected = !!companySettings?.r365Subdomain && role === 'admin';
  const [pushToR365, setPushToR365] = useState(false);

  useEffect(() => {
    if (!r365Connected) return;
    if (companySettings.r365DefaultGLAccount && companySettings.r365DefaultLocation) {
      setPushToR365(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r365Connected]);

  const confirmedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    try {
      setPhase('uploading');
      const [{ uploadUrl, key }] = await Promise.all([
        receiptScanService.getUploadUrl(file.name, file.type),
      ]);
      await receiptScanService.uploadToS3(uploadUrl, file);

      setPhase('scanning');
      const [result, currentPrices] = await Promise.all([
        receiptScanService.scanReceipt({ documentKey: key, documentOriginalName: file.name, contentType: file.type }),
        ingredientService.getCurrentPrices().catch(() => []),
      ]);

      const pMap = {};
      for (const p of (currentPrices || [])) pMap[p.ingredientId] = Number(p.pricePerKg);
      setPriceMap(pMap);

      setScanId(result.id);
      setMeta({
        extractedSupplierName: result.extractedSupplierName,
        extractedInvoiceNumber: result.extractedInvoiceNumber,
        extractedInvoiceTotal: result.extractedInvoiceTotal,
        extractionNotes: result.extractionNotes,
      });
      setSupplierId(result.suggestedSupplierId ? String(result.suggestedSupplierId) : '');
      setDeliveryDate(result.extractedInvoiceDate || today());
      setTaxAmount(result.extractedInvoiceTax != null ? String(Math.round(result.extractedInvoiceTax * 100) / 100) : '');
      setLines((result.lineItems || []).map((li, idx) => {
        const ingredient = li.matchedIngredientId ? ingredients.find(i => String(i.id) === String(li.matchedIngredientId)) : null;
        const unit = li.unit === 'lb' ? 'lb' : 'kg';
        const quantity = li.quantityKg != null
          ? (unit === 'lb' ? li.quantityKg / LB_TO_KG : li.quantityKg)
          : (li.quantity != null ? li.quantity : '');
        return {
          key: idx,
          // Lines the AI couldn't match to a real ingredient are very likely non-ingredient
          // purchases (fuel, supplies, personal items) — default them out of the receiving log,
          // but keep the row fully editable so a wrong AI miss can still be matched manually.
          excludeFromReceiving: !li.matchedIngredientId,
          description: li.description,
          ingredientId: li.matchedIngredientId ? String(li.matchedIngredientId) : '',
          manufacturerId: ingredient?.manufacturerId ? String(ingredient.manufacturerId) : '',
          unit,
          lotCodes: [{ lotCode: '', quantityKg: quantity !== '' ? String(quantity) : '' }],
          pricePerKg: li.impliedPricePerKg != null ? String(Math.round(li.impliedPricePerKg * 10000) / 10000) : '',
          lineTotal: li.lineTotal != null ? String(Math.round(li.lineTotal * 100) / 100) : '',
          notes: li.notes || '',
          matchConfidence: li.matchConfidence,
        };
      }));
      setPhase('review');
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Something went wrong reading this receipt.');
      setPhase('error');
    }
  };

  const setLine = (key, field, value) => {
    setLines(prev => prev.map(l => (l.key === key ? { ...l, [field]: value } : l)));
  };

  // Entering a per-kg price directly means doing the math by hand against the invoice — easier
  // to type the line's total $ (printed on the receipt) and let price/unit derive from it. Both
  // fields stay editable and sync off whichever one was just typed; quantity/unit changes re-derive
  // price/unit from the total when a total is present (it's closer to "ground truth" than a
  // previously-typed price), otherwise from price/unit.
  const deriveLinePriceFields = (next) => {
    const qtyKg = sumLotQtyKg(next.lotCodes, next.unit);
    const total = parseFloat(next.lineTotal);
    if (qtyKg > 0 && !isNaN(total) && next.lineTotal !== '') {
      next.pricePerKg = String(Math.round((total / qtyKg) * 10000) / 10000);
    } else {
      const price = parseFloat(next.pricePerKg);
      if (qtyKg > 0 && !isNaN(price)) {
        next.lineTotal = String(Math.round(price * qtyKg * 100) / 100);
      }
    }
    return next;
  };

  const setLinePriceField = (key, field, value) => {
    setLines(prev => prev.map(l => {
      if (l.key !== key) return l;
      const next = { ...l, [field]: value };
      if (field === 'lineTotal') {
        const qtyKg = sumLotQtyKg(next.lotCodes, next.unit);
        const total = parseFloat(value);
        if (qtyKg > 0 && !isNaN(total)) next.pricePerKg = String(Math.round((total / qtyKg) * 10000) / 10000);
      } else if (field === 'pricePerKg') {
        const qtyKg = sumLotQtyKg(next.lotCodes, next.unit);
        const price = parseFloat(value);
        if (qtyKg > 0 && !isNaN(price)) next.lineTotal = String(Math.round(price * qtyKg * 100) / 100);
      }
      return next;
    }));
  };

  const setLineUnit = (key, unit) => {
    setLines(prev => prev.map(l => (l.key === key ? deriveLinePriceFields({ ...l, unit }) : l)));
  };

  const setLineLotCode = (key, idx, field, value) => {
    setLines(prev => prev.map(l => {
      if (l.key !== key) return l;
      const lotCodes = l.lotCodes.map((lc, i) => (i === idx ? { ...lc, [field]: value } : lc));
      return deriveLinePriceFields({ ...l, lotCodes });
    }));
  };

  const addLotCodeRow = (key) => {
    setLines(prev => prev.map(l => (l.key === key ? { ...l, lotCodes: [...l.lotCodes, { lotCode: '', quantityKg: '' }] } : l)));
  };

  const removeLotCodeRow = (key, idx) => {
    setLines(prev => prev.map(l => {
      if (l.key !== key || l.lotCodes.length <= 1) return l;
      return deriveLinePriceFields({ ...l, lotCodes: l.lotCodes.filter((_, i) => i !== idx) });
    }));
  };

  const manualKeyRef = useRef(1000000);
  const addManualLine = () => {
    const key = manualKeyRef.current++;
    setLines(prev => [...prev, {
      key,
      excludeFromReceiving: false,
      isManual: true,
      description: '',
      ingredientId: '',
      manufacturerId: '',
      unit: 'kg',
      lotCodes: [{ lotCode: '', quantityKg: '' }],
      pricePerKg: '',
      lineTotal: '',
      notes: '',
      matchConfidence: null,
    }]);
  };

  // Removes any line outright — AI-extracted or manually added. Previously only manual lines could
  // be removed; an AI-extracted line with a genuinely $0 value (nothing delivered/charged) had no
  // way to disappear from the review table short of excluding it, which required an amount anyway.
  const removeLine = (key) => setLines(prev => prev.filter(l => l.key !== key));

  const quantityKgFor = (line) => sumLotQtyKg(line.lotCodes, line.unit);

  const pctDiffFor = (line) => {
    const price = parseFloat(line.pricePerKg);
    const lastPrice = line.ingredientId ? priceMap[Number(line.ingredientId)] : null;
    if (!price || isNaN(price) || lastPrice == null || lastPrice <= 0) return null;
    return ((price - lastPrice) / lastPrice) * 100;
  };

  const handleClose = () => {
    if (confirmedRef.current) {
      onConfirmed();
    } else if (scanId) {
      receiptScanService.discardScan(scanId).catch(() => {});
    }
    onClose();
  };

  const validate = () => {
    if (!supplierId) return 'Supplier is required.';
    if (!deliveryDate) return 'Delivery date is required.';
    if (deliveryDate < '2015-01-01' || deliveryDate > maxDeliveryDate) {
      return `Delivery date "${deliveryDate}" doesn't look right — AI-read receipt dates aren't always correct, please double-check it.`;
    }
    if (lines.length === 0) return 'At least one line item is required.';
    for (const l of lines) {
      const label = l.description || 'the manually added line';
      if (l.excludeFromReceiving) {
        if (!l.description || !l.description.trim()) return 'Enter a description for the excluded line left blank.';
        const amt = parseFloat(l.lineTotal);
        // 0 is a legitimate amount here (e.g. a line that's on the receipt but was never actually
        // delivered — backordered/substituted/credited) — only reject blank, non-numeric, or negative.
        if (l.lineTotal === '' || l.lineTotal == null || isNaN(amt) || amt < 0) return `Enter an amount for "${label}" (0 is fine if nothing was delivered/charged).`;
      } else {
        if (!l.ingredientId) return `Select an ingredient for "${label}", or check "Exclude from receiving".`;
        const qty = quantityKgFor(l);
        if (!qty || qty <= 0) return `Enter a valid quantity for "${label}".`;
      }
    }
    return null;
  };

  const handleConfirm = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setPhase('confirming');
    try {
      const selectedBankAccount = qboBankAccounts.find(a => String(a.id) === String(qbPurchaseAccountId));
      const receivingLines = lines.filter(l => !l.excludeFromReceiving);
      const excludedLines = lines.filter(l => l.excludeFromReceiving);
      const payload = {
        supplierId,
        deliveryDate,
        receivedByName,
        lines: receivingLines.map(l => {
          const qtyKg = quantityKgFor(l);
          const pricePerKg = l.pricePerKg !== '' ? parseFloat(l.pricePerKg) : null;
          const validLotCodes = l.lotCodes
            .filter(lc => lc.lotCode.trim() && lc.quantityKg !== '')
            .map(lc => ({ lotCode: lc.lotCode.trim(), quantityKg: qtyKgOf(lc.quantityKg, l.unit) }));
          return {
            ingredientId: Number(l.ingredientId),
            manufacturerId: l.manufacturerId ? Number(l.manufacturerId) : null,
            totalQuantityKg: qtyKg,
            unitCost: pricePerKg,
            lotCodes: validLotCodes,
            notes: l.notes || null,
          };
        }),
        excludedLines: excludedLines.map(l => ({
          description: l.description.trim(),
          amount: parseFloat(l.lineTotal),
        })),
        pushToQbo: pushToQbo && !!qbPurchaseAccountId,
        qbPurchaseAccountId: qbPurchaseAccountId || null,
        qbPurchaseAccountSubType: selectedBankAccount?.subType || null,
        pushToR365,
        taxAmount: taxAmount !== '' ? parseFloat(taxAmount) : null,
      };
      const result = await receiptScanService.confirmScan(scanId, payload);
      confirmedRef.current = true;
      setConfirmResult(result);
      setPhase('done');
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to create receiving logs.');
      setPhase('review');
    }
  };

  // Sums every line regardless of exclude state — the whole point of excluding a non-ingredient
  // line from the receiving log is that its amount still needs to count toward the receipt total.
  const lineTotalsSum = lines
    .reduce((sum, l) => {
      const total = parseFloat(l.lineTotal);
      if (!isNaN(total)) return sum + total;
      const qtyKg = quantityKgFor(l);
      const price = parseFloat(l.pricePerKg);
      return sum + (qtyKg && price ? qtyKg * price : 0);
    }, 0);
  const taxNum = taxAmount !== '' ? parseFloat(taxAmount) : 0;
  const grandTotal = lineTotalsSum + (isNaN(taxNum) ? 0 : taxNum);

  return (
    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header" style={{ background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)', borderBottom: 'none' }}>
            <h5 className="modal-title text-white">📷 Scan Receipt / Invoice</h5>
            <button type="button" className="btn-close btn-close-white" onClick={handleClose} disabled={phase === 'confirming'} />
          </div>
          <div className="modal-body">
            {(phase === 'uploading' || phase === 'scanning') && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" />
                <div className="text-muted">{phase === 'uploading' ? 'Uploading receipt…' : 'Reading line items with AI…'}</div>
              </div>
            )}

            {phase === 'error' && (
              <div className="alert alert-danger">{error}</div>
            )}

            {(phase === 'review' || phase === 'confirming') && (
              <>
                {error && <div className="alert alert-danger py-2">{error}</div>}
                {meta?.extractionNotes && (
                  <div className="alert alert-warning py-2 small">{meta.extractionNotes}</div>
                )}

                <div className="card mb-3" style={{ borderTop: '3px solid #0d6efd', border: '1px solid #e9ecef', borderTopWidth: 3, borderTopColor: '#0d6efd' }}>
                  <div className="card-body">
                    <div className="text-uppercase text-muted mb-2" style={{ fontSize: 11, letterSpacing: 1, fontWeight: 700 }}>
                      📋 Receipt Details
                    </div>
                    <div className="row g-3">
                      <div className="col-md-5">
                        <label className="form-label">Supplier <span className="text-danger">*</span></label>
                        <SupplierPickerField
                          options={suppliers}
                          value={supplierId}
                          onChange={setSupplierId}
                          onCreated={fetchSuppliers}
                          placeholder={meta?.extractedSupplierName || 'Select or type to add a supplier…'}
                        />
                        {meta?.extractedSupplierName && (
                          <div className="form-text">Extracted from receipt: <strong>{meta.extractedSupplierName}</strong></div>
                        )}
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Delivery Date <span className="text-danger">*</span></label>
                        <input type="date" className="form-control" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} min="2015-01-01" max={maxDeliveryDate} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Received By</label>
                        <input type="text" className="form-control" value={receivedByName} onChange={e => setReceivedByName(e.target.value)} placeholder="Your name" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-uppercase text-muted mb-2" style={{ fontSize: 11, letterSpacing: 1, fontWeight: 700 }}>
                  🧾 Line Items
                </div>

                {/* Mobile: one stacked, fully-labeled card per line item — the desktop table below
                    is a fixed 6-column layout with real inputs in every cell, so unlike a read-only
                    table there's no column to simply hide on a small screen without losing a
                    control; a full card reflow (same state/handlers, different markup) is the only
                    way every field stays usable and legible on a phone. */}
                <div className="d-md-none">
                  {lines.map(line => (
                    <div key={line.key} className={`card mb-2 ${line.excludeFromReceiving ? 'border-warning' : ''}`}>
                      <div className="card-body p-2">
                        <div className="mb-2" style={{ fontSize: 11 }}>
                          {line.isManual ? (
                            <span className="text-muted">✎ Manually added</span>
                          ) : (
                            <>
                              <span className="text-muted">Receipt says: </span>
                              <code style={{ background: '#f8f9fa', padding: '0 5px', borderRadius: 3 }}>{line.description}</code>
                            </>
                          )}
                        </div>
                        {line.isManual && (
                          <input type="text" className="form-control form-control-sm mb-2" placeholder="Description"
                            value={line.description} onChange={e => setLine(line.key, 'description', e.target.value)} />
                        )}

                        <label className="form-label small mb-1">Ingredient</label>
                        <IngredientLinePicker
                          ingredients={ingredients}
                          value={line.ingredientId}
                          onChange={v => setLine(line.key, 'ingredientId', v)}
                        />
                        {line.ingredientId && line.matchConfidence && line.matchConfidence !== 'high' && (
                          <div className="form-text text-warning">⚠ Low-confidence match — please verify</div>
                        )}

                        <div style={{ opacity: line.excludeFromReceiving ? 0.4 : 1 }}>
                          <label className="form-label small mb-1 mt-2">Manufacturer</label>
                          <SupplierPickerField
                            options={suppliers}
                            value={line.manufacturerId}
                            onChange={v => setLine(line.key, 'manufacturerId', v)}
                            onCreated={fetchSuppliers}
                            placeholder="Manufacturer…"
                          />

                          <label className="form-label small mb-1 mt-2">Quantity / Lot Code(s)</label>
                          <div className="d-flex align-items-center gap-1 mb-1">
                            <span className="text-muted" style={{ fontSize: 10 }}>Unit:</span>
                            <select className="form-select form-select-sm" style={{ width: 68 }} value={line.unit}
                              onChange={e => setLineUnit(line.key, e.target.value)}>
                              <option value="kg">kg</option>
                              <option value="lb">lb</option>
                            </select>
                          </div>
                          {line.lotCodes.map((lc, i) => (
                            <div key={i} className="d-flex gap-1 mb-1 align-items-center">
                              <input type="text" className="form-control form-control-sm" placeholder="Lot code (optional)"
                                value={lc.lotCode} onChange={e => setLineLotCode(line.key, i, 'lotCode', e.target.value)} />
                              <input type="number" className="form-control form-control-sm" style={{ maxWidth: 70 }} placeholder="Qty"
                                value={lc.quantityKg} onChange={e => setLineLotCode(line.key, i, 'quantityKg', e.target.value)} />
                              <button type="button" className="btn btn-sm btn-outline-danger" style={{ padding: '0 6px' }}
                                onClick={() => removeLotCodeRow(line.key, i)} disabled={line.lotCodes.length <= 1}>
                                &times;
                              </button>
                            </div>
                          ))}
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <button type="button" className="btn btn-outline-secondary" style={{ fontSize: 11, padding: '1px 6px' }}
                              onClick={() => addLotCodeRow(line.key)}>
                              + Lot
                            </button>
                            <span className="text-muted" style={{ fontSize: 11 }}>
                              Total: {quantityKgFor(line).toFixed(2)} kg
                            </span>
                          </div>

                          <div className="row g-2">
                            <div className="col-6">
                              <label className="form-label small mb-1">Line Total</label>
                              <div className="input-group input-group-sm">
                                <span className="input-group-text">$</span>
                                <input type="number" className="form-control" value={line.lineTotal}
                                  onChange={e => setLinePriceField(line.key, 'lineTotal', e.target.value)} />
                              </div>
                            </div>
                            <div className="col-6">
                              <label className="form-label small mb-1">Price / unit</label>
                              <div className="input-group input-group-sm">
                                <span className="input-group-text">$</span>
                                <input type="number" className="form-control" value={line.pricePerKg}
                                  onChange={e => setLinePriceField(line.key, 'pricePerKg', e.target.value)} />
                              </div>
                            </div>
                          </div>

                          <div className="mt-2">
                            <PctDiffBadge pct={pctDiffFor(line)} />
                          </div>
                        </div>

                        <div className="form-check mt-2">
                          <input type="checkbox" className="form-check-input" id={`excl-m-${line.key}`}
                            checked={line.excludeFromReceiving}
                            onChange={e => setLine(line.key, 'excludeFromReceiving', e.target.checked)} />
                          <label className="form-check-label" htmlFor={`excl-m-${line.key}`} style={{ fontSize: 11 }}>
                            Exclude from receiving
                          </label>
                        </div>
                        <button type="button" className="btn btn-link btn-sm text-danger p-0"
                          style={{ fontSize: 11, textDecoration: 'none' }}
                          onClick={() => removeLine(line.key)}>
                          remove this line
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="table-responsive d-none d-md-block">
                <table className="table table-sm align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Line / Ingredient</th>
                      <th style={{ width: 160 }}>Manufacturer</th>
                      <th style={{ width: 240 }}>Quantity / Lot Code(s)</th>
                      <th style={{ width: 130 }}>
                        Line Total <span className="text-muted" style={{ cursor: 'help' }} title="What the receipt shows">ⓘ</span>
                      </th>
                      <th style={{ width: 130 }}>
                        Price / unit <span className="text-muted" style={{ cursor: 'help' }} title="Per kg, auto-computed from Line Total and quantity">ⓘ</span>
                      </th>
                      <th style={{ width: 150 }}>vs. Last Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map(line => (
                      <tr key={line.key} className={line.excludeFromReceiving ? 'table-warning' : ''}>
                        <td className="align-top py-2">
                          <div className="mb-1" style={ROW_LABEL_STYLE} title={line.isManual ? undefined : line.description}>
                            {line.isManual ? (
                              <span className="text-muted">✎ Manually added</span>
                            ) : (
                              <>
                                <span className="text-muted">Receipt says: </span>
                                <code style={{ background: '#f8f9fa', padding: '0 5px', borderRadius: 3 }}>
                                  {line.description}
                                </code>
                              </>
                            )}
                          </div>
                          {line.isManual && (
                            <input type="text" className="form-control form-control-sm mb-1" placeholder="Description"
                              value={line.description} onChange={e => setLine(line.key, 'description', e.target.value)} />
                          )}
                          <IngredientLinePicker
                            ingredients={ingredients}
                            value={line.ingredientId}
                            onChange={v => setLine(line.key, 'ingredientId', v)}
                          />
                          {line.ingredientId && line.matchConfidence && line.matchConfidence !== 'high' && (
                            <div className="form-text text-warning">⚠ Low-confidence match — please verify</div>
                          )}
                          <div className="form-check mt-1">
                            <input type="checkbox" className="form-check-input" id={`excl-${line.key}`}
                              checked={line.excludeFromReceiving}
                              onChange={e => setLine(line.key, 'excludeFromReceiving', e.target.checked)} />
                            <label className="form-check-label" htmlFor={`excl-${line.key}`} style={{ fontSize: 11 }}>
                              Exclude from receiving
                            </label>
                          </div>
                          <button type="button" className="btn btn-link btn-sm text-danger p-0"
                            style={{ fontSize: 11, textDecoration: 'none' }}
                            onClick={() => removeLine(line.key)}>
                            remove this line
                          </button>
                        </td>
                        <td className="align-top py-2" style={{ opacity: line.excludeFromReceiving ? 0.4 : 1 }}>
                          <div className="mb-1" style={ROW_LABEL_STYLE}>&nbsp;</div>
                          <SupplierPickerField
                            options={suppliers}
                            value={line.manufacturerId}
                            onChange={v => setLine(line.key, 'manufacturerId', v)}
                            onCreated={fetchSuppliers}
                            placeholder="Manufacturer…"
                          />
                        </td>
                        <td className="align-top py-2" style={{ opacity: line.excludeFromReceiving ? 0.4 : 1 }}>
                          <div className="mb-1" style={ROW_LABEL_STYLE}>&nbsp;</div>
                          <div className="d-flex align-items-center gap-1 mb-1">
                            <span className="text-muted" style={{ fontSize: 10 }}>Unit:</span>
                            <select className="form-select form-select-sm" style={{ width: 68 }} value={line.unit}
                              onChange={e => setLineUnit(line.key, e.target.value)}>
                              <option value="kg">kg</option>
                              <option value="lb">lb</option>
                            </select>
                          </div>
                          {line.lotCodes.map((lc, i) => (
                            <div key={i} className="d-flex gap-1 mb-1 align-items-center">
                              <input type="text" className="form-control form-control-sm" placeholder="Lot code (optional)"
                                value={lc.lotCode} onChange={e => setLineLotCode(line.key, i, 'lotCode', e.target.value)} />
                              <input type="number" className="form-control form-control-sm" style={{ maxWidth: 65 }} placeholder="Qty"
                                value={lc.quantityKg} onChange={e => setLineLotCode(line.key, i, 'quantityKg', e.target.value)} />
                              <button type="button" className="btn btn-sm btn-outline-danger" style={{ padding: '0 6px' }}
                                onClick={() => removeLotCodeRow(line.key, i)} disabled={line.lotCodes.length <= 1}>
                                &times;
                              </button>
                            </div>
                          ))}
                          <div className="d-flex align-items-center gap-2">
                            <button type="button" className="btn btn-outline-secondary" style={{ fontSize: 11, padding: '1px 6px' }}
                              onClick={() => addLotCodeRow(line.key)}>
                              + Lot
                            </button>
                            <span className="text-muted" style={{ fontSize: 11 }}>
                              Total: {quantityKgFor(line).toFixed(2)} kg
                            </span>
                          </div>
                        </td>
                        <td className="align-top py-2">
                          <div className="mb-1" style={ROW_LABEL_STYLE}>&nbsp;</div>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <input type="number" className="form-control" style={{ minWidth: 80 }} value={line.lineTotal}
                              onChange={e => setLinePriceField(line.key, 'lineTotal', e.target.value)} />
                          </div>
                        </td>
                        <td className="align-top py-2" style={{ opacity: line.excludeFromReceiving ? 0.4 : 1 }}>
                          <div className="mb-1" style={ROW_LABEL_STYLE}>&nbsp;</div>
                          <div className="input-group input-group-sm">
                            <span className="input-group-text">$</span>
                            <input type="number" className="form-control" style={{ minWidth: 70 }} value={line.pricePerKg}
                              onChange={e => setLinePriceField(line.key, 'pricePerKg', e.target.value)} />
                          </div>
                        </td>
                        <td className="align-top py-2" style={{ opacity: line.excludeFromReceiving ? 0.4 : 1 }}>
                          <div className="mb-1" style={ROW_LABEL_STYLE}>&nbsp;</div>
                          <PctDiffBadge pct={pctDiffFor(line)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>

                <div className="mb-4">
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={addManualLine}>
                    + Add Line Item
                  </button>
                  <span className="text-muted ms-2" style={{ fontSize: 12 }}>In case the AI missed something on the receipt.</span>
                </div>

                <div className="card mb-3" style={{
                  border: '1px solid #e9ecef',
                  borderTopWidth: 3,
                  borderTopStyle: 'solid',
                  borderTopColor: (meta?.extractedInvoiceTotal != null && Math.abs(grandTotal - Number(meta.extractedInvoiceTotal)) > 0.5) ? '#fd7e14' : '#198754',
                }}>
                  <div className="card-body">
                    <div className="row g-3 align-items-end">
                      <div className="col-md-3">
                        <label className="form-label mb-1" style={{ fontSize: 13 }}>Sales Tax</label>
                        <div className="input-group input-group-sm">
                          <span className="input-group-text">$</span>
                          <input type="number" className="form-control" value={taxAmount}
                            onChange={e => setTaxAmount(e.target.value)} placeholder="0.00" />
                        </div>
                      </div>
                      <div className="col-md-9 small">
                        <span className="text-muted">Lines total</span> <strong>${lineTotalsSum.toFixed(2)}</strong>
                        {taxNum > 0 && <> <span className="text-muted">+ tax</span> <strong>${taxNum.toFixed(2)}</strong> <span className="text-muted">=</span> <strong className="text-success">${grandTotal.toFixed(2)}</strong></>}
                        {meta?.extractedInvoiceTotal != null && (
                          <>
                            {' '}<span className="text-muted">— receipt total:</span> <strong>${Number(meta.extractedInvoiceTotal).toFixed(2)}</strong>
                            {Math.abs(grandTotal - Number(meta.extractedInvoiceTotal)) > 0.5 ? (
                              <span className="text-warning"> ⚠ these don't match, double-check quantities/prices/tax above.</span>
                            ) : (
                              <span className="text-success"> ✓ matches</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {qboConnected && (
                  <div className="rounded p-3 mb-3" style={{ background: '#eaf4ff', border: '1px solid #b6daff' }}>
                    <div className="form-check mb-2">
                      <input
                        type="checkbox" className="form-check-input" id="pushToQboCheck"
                        checked={pushToQbo} onChange={e => setPushToQbo(e.target.checked)}
                      />
                      <label className="form-check-label small fw-semibold" htmlFor="pushToQboCheck">
                        Also push this expense to QuickBooks
                      </label>
                    </div>
                    {!pushToQbo && (
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        You can leave this unchecked and push it to QuickBooks later from the Receiving Log list —
                        useful if you don't have QuickBooks access yourself.
                      </div>
                    )}
                    {pushToQbo && (
                      <div style={{ maxWidth: 320 }}>
                        <label className="form-label small mb-1">Purchase Account (card/bank this was paid with)</label>
                        <SearchableSelect
                          options={qboBankAccounts.map(a => ({ value: a.id, label: a.name }))}
                          value={qbPurchaseAccountId}
                          onChange={setQbPurchaseAccountId}
                          placeholder="Select an account…"
                          size="sm"
                        />
                        {taxNum > 0 && !companySettings?.qbSalesTaxAccountId && (
                          <div className="text-warning mt-1" style={{ fontSize: 11 }}>
                            ⚠ No Sales Tax Expense Account configured — set one in Company Settings or the ${taxNum.toFixed(2)} tax push will fail.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {!qboConnected && companySettings?.qbRealmId && (
                  <div className="text-muted small">
                    ℹ An admin will push this receipt to QuickBooks later from the Receiving Log list.
                  </div>
                )}

                {r365Connected && (
                  <div className="rounded p-3 mb-3" style={{ background: '#eaf4ff', border: '1px solid #b6daff' }}>
                    <div className="form-check mb-1">
                      <input
                        type="checkbox" className="form-check-input" id="pushToR365Check"
                        checked={pushToR365} onChange={e => setPushToR365(e.target.checked)}
                      />
                      <label className="form-check-label small fw-semibold" htmlFor="pushToR365Check">
                        Also push this expense to Restaurant 365
                      </label>
                    </div>
                    {!pushToR365 && (
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        You can leave this unchecked and push it to Restaurant 365 later from the Receiving Log list.
                      </div>
                    )}
                    {pushToR365 && !companySettings?.r365DefaultLocation && (
                      <div className="text-warning mt-1" style={{ fontSize: 11 }}>
                        ⚠ No Default Location configured — set one in Company Settings or this push will fail.
                      </div>
                    )}
                  </div>
                )}
                {!r365Connected && companySettings?.r365Subdomain && (
                  <div className="text-muted small">
                    ℹ An admin will push this receipt to Restaurant 365 later from the Receiving Log list.
                  </div>
                )}
              </>
            )}

            {phase === 'done' && (
              <div className="text-center py-5">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{ width: 64, height: 64, background: '#d1f0dd', fontSize: 32 }}>
                  ✅
                </div>
                <div className="fw-semibold mb-2" style={{ fontSize: 17 }}>
                  Created {confirmResult?.receivingLogIds?.length || 0} receiving log{confirmResult?.receivingLogIds?.length === 1 ? '' : 's'}.
                </div>
                {confirmResult?.qboPurchaseId && (
                  <div className="text-success small">✓ Pushed to QuickBooks as Purchase #{confirmResult.qboPurchaseId}</div>
                )}
                {confirmResult?.qboPushError && (
                  <div className="alert alert-warning text-start mt-3 py-2 small">
                    Receiving logs were created, but the QuickBooks push failed: {confirmResult.qboPushError}
                  </div>
                )}
                {confirmResult?.r365InvoiceId && (
                  <div className="text-success small">✓ Pushed to Restaurant 365 as Invoice {confirmResult.r365InvoiceId}</div>
                )}
                {confirmResult?.r365PushError && (
                  <div className="alert alert-warning text-start mt-3 py-2 small">
                    Receiving logs were created, but the Restaurant 365 push failed: {confirmResult.r365PushError}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            {phase !== 'done' && (
              <button className="btn btn-outline-secondary" onClick={handleClose} disabled={phase === 'confirming'}>Cancel</button>
            )}
            {phase === 'review' || phase === 'confirming' ? (
              <button className="btn btn-primary" onClick={handleConfirm} disabled={phase === 'confirming'}>
                {phase === 'confirming'
                  ? <><span className="spinner-border spinner-border-sm me-2" />Creating…</>
                  : (() => {
                      const n = lines.filter(l => !l.excludeFromReceiving).length;
                      return `Confirm & Create ${n} Receiving Log${n === 1 ? '' : 's'}`;
                    })()}
              </button>
            ) : phase === 'done' ? (
              <button className="btn btn-primary" onClick={handleClose}>Done</button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
